/** Phase 8 (finance & analytics) root strings — English: error codes and permission names (ADR-801). */
export const faz8ErrorsEn = {
  shipment_cost_exists: 'A shipping cost is already recorded for this shipment; edit the existing entry.',
  vat_exceeds_amount: 'VAT cannot exceed the total amount.',
  tracking_not_found: 'No shipment with this tracking number.',
  tracking_ambiguous: 'This tracking number belongs to several shipments; enter the cost on the shipment.',
  duplicate_in_file: 'This tracking number appears more than once in the file.',
  amount_required: 'The amount is empty or unreadable.',
  date_unreadable: 'Unreadable date (DD.MM.YYYY or YYYY-MM-DD).',
  cost_file_columns_missing: 'The file has no "Tracking no" and "Amount" columns.',
  supplier_code_exists: 'A supplier with this code already exists.',
  supplier_inactive: 'The supplier is inactive; activate it first.',
  purchase_order_not_editable:
    'After the order is placed, lines, warehouse and currency cannot change; only the expected date and note can.',
  purchase_order_status: 'This action is not possible in the order’s current status.',
  purchase_order_not_receivable: 'Goods can be received only for placed orders that are not fully received.',
  warehouse_inactive: 'The warehouse is inactive; activate it to receive goods.',
  duplicate_variant: 'The same product cannot be on more than one line; enter the quantity on one line.',
  over_receipt: 'Received quantity exceeds the remaining quantity; tick the option to accept over-delivery.',
  duplicate_line: 'The same order line cannot appear twice in one receipt.',
  lot_expiry_mismatch: 'This lot number is registered with a different expiry date.',
  supplier_entry_not_reversible:
    'A goods-receipt invoice cannot be reversed; correct the difference with an adjustment. A reversal cannot be reversed.',
  report_metric_invalid: 'This metric is not available in the selected dataset.',
  report_dimension_invalid: 'This dimension is not available in the selected dataset.',
  report_duplicate: 'The same metric or dimension cannot be chosen twice.',
  report_pivot_one_metric: 'With a column dimension (pivot), choose a single metric.',
  report_filter_invalid: 'This filter cannot be used with the selected dataset.',
  report_definition_invalid: 'The report definition could not be read.',
  dashboard_widget_limit: 'You can add up to {{max}} widgets to the dashboard.',
  dashboard_widget_exists: 'This report is already on your dashboard.',
  widget_order_mismatch: 'The order must contain every widget exactly once.',
  report_export_not_ready: 'The file is not ready yet or could not be produced.',
  report_export_too_large: 'The file exceeds the 20 MB limit; add a filter or reduce the row dimensions.',
  report_member_inactive: "The user's membership is not active; no file was produced.",
  no_eligible_recipient: 'No recipient is permitted to see this report; no email was sent.',
  payment_not_disputable:
    'A dispute can only be opened for a captured card (iyzico, PayTR) or physical POS payment.',
  dispute_exceeds_payment: 'The dispute amount exceeds the undisputed amount of the payment.',
  dispute_fee_too_large: 'The dispute fee cannot exceed the payment amount.',
  dispute_case_exists: 'A dispute with this case number already exists for this payment.',
  dispute_status_transition: 'The dispute cannot move from its current status to this one.',
  date_before_opened: 'This date cannot be before the dispute was opened.',
  date_after_closed: 'The opening date cannot be after the closing date.',
  simulation_currency_unsupported: 'Price simulation currently supports TRY price lists only.',
  // Automation builder (F8-15)
  workflow_field_invalid: 'This field does not exist for the selected trigger.',
  workflow_operator_invalid: 'This comparison cannot be used with this field.',
  workflow_value_invalid: 'The value does not fit this field (a number, option or text is expected).',
  workflow_actions_required: 'Add at least one action.',
  workflow_action_incompatible: 'This action cannot be used with the selected trigger.',
  workflow_recipient_invalid: 'One of the recipients is not an active member of the business.',
  saved_report_not_found: 'The saved report was not found or you cannot see it.',
  workflow_template_linked:
    'This example messages customers; it is managed in its own section for consent and IYS checks.',
  workflow_archived: 'An archived flow cannot be changed or turned on.',
  workflow_active: 'An active flow cannot be archived; pause it first.',
  workflow_run_not_retryable: 'Only a failed run can be retried.',
  workflow_not_active: 'The flow is not on; turn it on before retrying.',
  workflow_run_not_cancellable: 'Only a pending run can be cancelled.',
  workflow_subject_missing: 'The event has no record this action needs.',
  owner_inactive: 'The membership of the person who turned the flow on is not active.',
  no_recipient: 'No active member to email with this setting.',
  seo_issues: 'There are SEO issues.',
  order_not_paid: 'The order is not in "paid" status; left unchanged.',
  order_not_found: 'Order not found.',
  product_not_found: 'Product not found.',
  post_not_found: 'Post not found.',
  already_published: 'The approved version is already live.',
  invoice_uncertain: 'The invoice result is uncertain; check BizimHesap and mark it on the Invoices page.',
  invoice_send_failed: 'The invoice could not be sent to BizimHesap; resend it from the Invoices page.',
  workflow_paused: 'The flow was paused; the pending run was cancelled.',
  manual_retry: 'The run was retried manually.',
  too_short: 'The text is too short.',
  invalid: 'Invalid value.',
} as const;

export const faz8PermissionsEn = {
  'reports.finance.manage': 'Manage finance inputs (payment fee rules, shipping costs, business expenses)',
  'purchasing.read': 'View suppliers and purchasing (including costs and supplier balances)',
  'purchasing.manage': 'Manage suppliers and purchase orders',
  'purchasing.receive': 'Receive goods (stock receipt against purchase orders)',
  'purchasing.ledger.manage': 'Supplier ledger entries (payments, returns, opening, adjustments)',
  'reports.custom.read': 'Run custom reports and view shared ones (data limited by own permissions)',
  'reports.custom.manage': 'Save and share custom reports',
  'reports.schedule.manage': 'Scheduled reports (email download links to organization members)',
  'payments.dispute.manage': 'Payment disputes (chargebacks): record, status, notes and documents',
  'workflows.read': 'View automation flows and their run log',
  'workflows.manage':
    'Create, turn on/pause and retry automation flows (actions run with the permissions of the person who turned it on)',
} as const;
